<template>
  <div class="grid">
    <DataTable
      v-if="rows.length > 0"
      :columnsConfig="columnsConfig"
      :data="rows"
      class="table-container fixed-header-table"
    >
      <template v-slot:cell="{ content, row, col, sub }">
        <template v-if="columnsConfig[col].key === 'categories'">
          <template v-for="(category, index) in content">
            <div :key="index">{{ category }}</div>
          </template>
        </template>
        <template v-else-if="columnsConfig[col].key === 'approvalStatus'">
          <div class="status-icon-container" :id="'status-row-' + row" @mouseleave="hideMultiTooltipOnLeave(row)">
            <StatusIcons
              :item-name="statusesItemsName"
              :status="content"
              @showToolTip="(event, message) => showTooltipAndLoadStatus(event, message, row, content)"
              @hideToolTip="hideToolTip"
            />
            <div class="clear-both"></div>
          </div>
          <mds-tooltip
            :triggered-by="'status-row-' + row"
            :visible="rows[row].toggle || false"
            :position="['bottom-right']"
          >
            <mds-loader v-if="!!rows[row].loadingStatus" size="small"/>
            <ul class="status-list" v-else>
              <li v-for="(status, index) in rows[row].statusDetails" :key="index" class="status-list-item">
                <span v-if="status.isPending">
                  <mds-button
                    class="button-icon pending-tooltip-icon"
                    variation="icon-only"
                    icon="clock-back"
                    type="button"
                  >
                  </mds-button>
                </span>
                <span>{{ status.statusText }}</span>
              </li>
            </ul>
          </mds-tooltip>
        </template>
        <template v-else-if="columnsConfig[col].key === 'displayName'">
          <div class="fund-series">
            <span>{{ content }}</span>
          </div>
        </template>
        <template v-else-if="columnsConfig[col].key === 'pending'">
          <template v-if="content">
            <mds-button
              class="button-icon"
              variation="icon-only"
              icon="clock-back"
              type="button"
              @mouseover="showToolTip($event, ['Pending'])"
              @mouseleave="hideToolTip"
            >
            </mds-button>
          </template>
          <template v-else>
            {{ '' }}
          </template>
        </template>
        <template v-else-if="columnsConfig[col].key === 'action'">
          <template v-if="content">
            <span
              v-if="mustShowAction(content, row, sub)"
              class="grid-universe__action-icon"
              :class="content"
              @click.prevent="actionsSchema[content].action(row, sub)"
            >
              <BaseGridIcon
                class="mds-icon grid-universe-button_fund-change-status"
                aria-hidden="true"
                :icon="actionsSchema[content].icon"
                :tooltipMessage="getTooltipMessage(content)"
                @showToolTip="showToolTip"
                @hideToolTip="hideToolTip"
              />
            </span>
            <span v-else>
              {{ '' }}
            </span>
          </template>
          <template v-else>
            {{ '' }}
          </template>
        </template>
        <template v-else-if="columnsConfig[col].key === 'reviewStatuses'">
          <div class="review-status-column"
          @click.prevent="redirectToReviewStatusAcrossClients(rows[row].fundID, rows[row].id)">
            <ReviewStatusIcons
              :status="content"
              @showToolTip="showToolTip"
              @hideToolTip="hideToolTip"
            />
          </div>
        </template>
        <template v-else>
          {{ content || "—" }}
        </template>
      </template>
    </DataTable>
    <div class="loading-spinner" v-if="isLoading">
      <mds-loader />
    </div>
    <p v-else-if="totalRows === 0" class="title-message">{{ emptyMessage }}</p>
  </div>
</template>
<script>
import {
  cloneDeep, concat, debounce, filter, get, includes, has, union,
} from 'lodash';
import slugify from 'slugify';
import { mapActions, mapState } from 'vuex';
import MdsTooltip from '@mds/tooltip';

import StatusIcons from '@/components/atoms/StatusIcons';
import BaseGridIcon from '@/components/atoms/BaseGridIcon';
import ReviewStatusIcons from '@/components/atoms/ReviewStatusIcons';
import DataTable from '@/components/molecules/data-table/table';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import * as UniverseFundsService from '@/services/UniverseFunds';
import gridUniverserMessages from '@/config/grid-universe-messages.json';
import axios from 'axios';
import { FUND_STATUSES, FIDUCIARIES, USER_ROLES } from '@/utils/app-constants';

const defaultColumnsConfig = require('@/config/all-client-grid-columns.json');

const columnsConfig = cloneDeep(defaultColumnsConfig);

const DEFAULT_FIRST_PAGE = 0;
const GRID_PAGE_SIZE = 50;

export default {
  components: {
    DataTable,
    StatusIcons,
    BaseGridIcon,
    ReviewStatusIcons,
    MdsTooltip,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'Organisms/GridUniverseGrid' }),
  ],
  props: {
    filters: {
      type: Object,
      required: true,
    },
    scrolled: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      columnsConfig,
      messages: Object.freeze(gridUniverserMessages),
      rows: [],
      currentPage: DEFAULT_FIRST_PAGE,
      totalPages: 1,
      requestController: null,
      requestUrl: '',
      queryInProcess: null,
      changeStatusFundsFlag: false,
      sortKey: 'displayName',
      sortColumn: 'fundName',
      abortRequestKey: 'AbortRequest',
      sortDirection: 1,
      actionsSchema: {
        empty: {
          icon: '',
          action: null,
          tooltipMessage: '',
        },
        approve: {
          icon: 'check',
          action: this.approve,
          tooltipMessage: 'Approve',
        },
        'change-status': {
          icon: 'caret-circle-down',
          action: this.changeStatus,
          tooltipMessage: 'Change Status',
        },
        preferred: {
          icon: 'ip-star-rating',
          action: this.preferred,
          tooltipMessage: 'Preferred Fund',
        },
      },
      additionalColumns: {
        newSales: {
          text: 'New Sales',
          key: 'newSales',
          sortColumn: 'opennewsales',
          width: 76,
          sortDirection: 0,
        },
        preferred: {
          text: 'Preferred',
          key: 'preferred',
          sortColumn: 'preferred',
          width: 64,
          sortDirection: 0,
        },
        reviewStatuses: {
          text: 'Review Status',
          key: 'reviewStatuses',
          width: 64,
        },
      },
    };
  },
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.grid.client.selected,
      selectedClientName: (state) => state.grid.client.name,
      selectedTab: (state) => state.grid.tab,
      searchTerm: (state) => state.grid.searchTerm,
      totalRows: (state) => state.grid.totalRows,
      fullscreenModalShow: (state) => state.fullscreenModal.show,
      fullscreenModalPerformAction: (state) => state.fullscreenModal.performAction,
      config: (state) => state.system.config,
      isLoading: (state) => state.system.isLoading,
      selectedFiduciaryCode: (state) => state.user.fiduciary.code,
      userRole: (state) => state.user.userRole,
    }),
    emptyMessage() {
      if (this.searchTerm) {
        return this.messages.search.emptyFundsList;
      }
      return this.messages.emptyFundsList;
    },
    isLogoutTheCurrentRoute() {
      return this.$route.name === 'logout';
    },
    statusesItemsName() {
      return {
        singular: 'client',
        plural: 'clients',
      };
    },
    currentViewName() {
      return this.selectedClient > 0 ? 'one-client' : 'all-clients';
    },
    localPolicyOptions() {
      // Tabs are applied to 1 Client View
      if (this.currentViewName === 'all-clients') {
        return get(this.policies, `grid.views.${this.currentViewName}`);
      }
      return get(this.policies, `grid.views.${this.currentViewName}.tabs.${this.selectedTab}`);
    },
    queryOptions() {
      const column = this.sortColumn;
      const direction = this.sortDirection === 1 ? 'asc' : 'desc';

      let parameters = `&pageSize=${GRID_PAGE_SIZE}&sortColumn=${column}&sortDirection=${direction}`;

      if (this.filters.status.length > 0) {
        const escapedParameters = this.filters.status.map(
          (item) => encodeURIComponent(item.toLowerCase()),
        );
        parameters += `&statusList=${escapedParameters}`;
      }
      if (this.filters.categories.length > 0) {
        const escapedParameters = this.filters.categories.map((item) => encodeURIComponent(item));
        parameters += `&category=${escapedParameters}`;
      }
      if (this.filters.assets.length > 0) {
        const escapedParameters = this.filters.assets.map((item) => encodeURIComponent(item));
        parameters += `&assetClassName=${escapedParameters}`;
      }
      if (this.filters.reviewStatus.length > 0) {
        const escapedParameters = this.filters.reviewStatus.map((item) => encodeURIComponent(item));
        parameters += `&reviewStatusList=${escapedParameters}`;
      }
      if (this.searchTerm) {
        const escapedParameters = encodeURIComponent(this.searchTerm);
        parameters += `&search=${escapedParameters}`;
      }
      return parameters;
    },
    filterStatusSize() {
      return this.filters.status.length;
    },
    filterCategoriesSize() {
      return this.filters.categories.length;
    },
    filterAssetsSize() {
      return this.filters.assets.length;
    },
    filterReviewStatusSize() {
      return this.filters.reviewStatus.length;
    },
    isOneClientView() {
      return this.selectedClient !== 0 && this.selectedClientName !== 'All Clients';
    },
    showFundReviewColumn() {
      // Show Fund Review Panel only if Fiduciary is Morningstar and User is Consultant.
      return this.selectedFiduciaryCode === FIDUCIARIES.MONRNINGSTAR && this.userRole === USER_ROLES.CONSULTANT;
    },
  },
  mounted() {
    // Load initial Data
    this.setIsLoading(true);
    this.loadChangeStatusFeatureFlag();
    this.loadData();
    this.debouncedSearchFunds = debounce(() => {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = DEFAULT_FIRST_PAGE;
      this.rows = [];
      this.setIsLoading(true);
      return this.loadData();
    }, 700);
  },
  methods: {
    ...mapActions([
      'logError',
      'setIsLoading',
      'setSelectedFundToBeAdded',
      'cleanSelectedFundsList',
    ]),
    includes,
    cleanRequest() {
      this.requestController = null;
    },
    loadData() {
      if (this.requestController) {
        this.requestController.cancel();
      }
      this.setIsLoading(true);
      this.requestController = axios.CancelToken.source();
      UniverseFundsService.getAllClientUniverseFundsList({
        selectedClient: this.selectedClient,
        selectedFiduciary: this.selectedFiduciary,
        currentPage: this.currentPage,
        queryOptions: this.queryOptions,
        axiosCancelToken: this.requestController?.token,
      }).then((result) => {
        this.cleanRequest();
        this.setIsLoading(true);
        if (result.status.success) {
          const { totalElements: totalItems = 0, totalPages = 1 } = result.data;
          this.totalPages = totalPages;
          const contentRows = result.data.content;
          if (contentRows.length > 0) {
            const data = this.formatData(contentRows);
            if (this.currentPage === DEFAULT_FIRST_PAGE) {
              this.rows = data;
            } else {
              this.rows = concat(this.rows, data);
            }
          }
          this.$store.dispatch('setTotalRows', totalItems);
        }
        this.setIsLoading(false);
      }).catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }
        this.cleanRequest();
        const httpStatus = (error?.response?.status) ? (error?.response?.status) : error?.status;
        if (httpStatus === 401) {
          this.$router.push('/login');
        } else if (error.message && error.message !== this.abortRequestKey) {
          this.notifyError({
            error,
            section: 'methods:loadData',
            data: { url: this.requestUrl },
            notificaction: {
              title: 'Universe Grid',
              message: `Fetching Data (Error: ${error.message})`,
            },
          });
          this.setIsLoading(false);
        }
      });
    },
    loadChangeStatusFeatureFlag() {
      UniverseFundsService.getAllClientChangeStatusViewFlag().then((result) => {
        if (result?.data?.changeStatusFundsFlag) {
          this.changeStatusFundsFlag = true;
        }
      }).catch(() => {
        this.changeStatusFundsFlag = false;
      });
    },
    approve(row) {
      this.$store.dispatch('openFullscreenModal', {
        title: 'Approve Fund',
        component: 'approve',
        data: this.rows[row],
      });
    },
    changeStatus(row, subRow) {
      let data = this.rows[row];
      if (undefined !== subRow) {
        data = get(this.rows[row], `subs[${subRow}]`, {});
      }
      // send change status fund flag
      data.isAllClientChangeStatus = this.changeStatusFundsFlag;
      this.$store.dispatch('openFullscreenModal', {
        title: 'Change Status',
        component: this.changeStatusFundsFlag ? 'all-client-change-status' : 'change-status',
        data,
      });
    },
    setSortBy() {
      this.columnsConfig = this.columnsConfig.map((col) => {
        const clonedCol = cloneDeep(col);
        if (col.key === this.sortKey) {
          clonedCol.sortDirection = this.sortDirection;
        } else if (col.sortDirection !== undefined) {
          clonedCol.sortDirection = 0;
        }
        return clonedCol;
      });
    },
    formatData(data) {
      // Reset columns
      this.columnsConfig = cloneDeep(defaultColumnsConfig);
      this.setSortBy();

      // Add review status column
      if (this.showFundReviewColumn) {
        this.columnsConfig.splice(-1, 0, this.additionalColumns.reviewStatuses);
      }

      this.columnsConfig = this.columnsConfig.map((col) => {
        const clonedCol = cloneDeep(col);
        if (clonedCol.sortDirection !== undefined) {
          clonedCol.action = () => {
            if (this.sortKey === clonedCol.key) {
              this.sortDirection *= -1;
            } else {
              this.sortDirection = 1;
            }
            this.sortKey = clonedCol.key;
            this.sortColumn = clonedCol.sortColumn;
            this.setSortBy();
          };
        }
        return clonedCol;
      });

      const formatedData = data
        .map((row, index) => {
          let newRow = {};
          try {
            const pendingStatus = this.formatPendingStatuses(row);
            const fundStatus = this.formatFundStatuses(row);
            const approvalStatus = this.formatApprovalStatus(
              row,
              fundStatus,
              pendingStatus,
            );
            // eslint-disable-next-line max-len
            const action = this.changeStatusFundsFlag ? this.formatActionsForChangeStatus() : this.formatActions(fundStatus, pendingStatus);
            const slug = slugify(`${row.fundName}-${row.secId}`);

            newRow = {
              id: row.fiduciaryFundID,
              index,
              fundID: row.fundID,
              slug,
              displayName: row.fundName,
              ticker: row.ticker,
              secID: row.secId,
              categories: row.category,
              fundStatus,
              pendingStatus,
              morningstarBroadAssetClass: row.broadAssetClassName,
              approvalStatus,
              reviewStatuses: row.reviewStatuses,
              pending: row.pendingStatusIDs.length > 0,
              pendingSeriesChange: row.fundSeries ? row.fundSeries.isPendingSeriesChange : false,
              action,
              newSales: row.isOpenToNewSales,
              preferred: row.isPreferred,
              subs: row.fundsSeries || [],
              __isTargetSeries: false,
            };
          } catch (e) {
            this.logError({
              data: row,
              errorFrom: 'AllClientView:methods:formatData',
              details: {
                ...Object.getOwnPropertyDescriptors(e),
                message: e.message,
              },
            });
          }
          return newRow;
        })
        .filter((row) => row.approvalStatus && row.approvalStatus.length > 0);
      return formatedData;
    },
    formatPendingStatuses(data) {
      // Process pending status
      const pendingStatusArr = data.pendingStatusIDs;
      let pendingStatuses = [];
      if (pendingStatusArr && pendingStatusArr.length > 0) {
        pendingStatuses = pendingStatusArr.map((item) => {
          return {
            id: item,
            name: FUND_STATUSES[item],
          };
        });
      }
      return pendingStatuses;
    },
    formatFundStatuses(data) {
      const fundStatusArr = data.productionStatusIDs;
      // Process pending status
      const fundStatuses = fundStatusArr.map((item) => {
        return {
          id: item,
          name: FUND_STATUSES[item],
        };
      });
      return fundStatuses;
    },
    formatApprovalStatus(rowData) {
      // Show combined (both pending and current) status in Status column
      const unionStatusArr = union(rowData.pendingStatusIDs, rowData.productionStatusIDs);
      const approvalStatus = unionStatusArr.map((item) => {
        return {
          id: item,
          name: FUND_STATUSES[item],
        };
      });
      return approvalStatus;
    },
    formatActions(currentData, pendingData) {
      // If has pendingStatus override production Status
      const data = (pendingData && pendingData.length > 0 ? pendingData : currentData) || [];

      // Get all statusses for Approve (None or Removed)
      const forApprove = filter(data,
        (status) => (status.name === 'Removed' || status.name === 'None'));

      // If statusses are for Approve else Change Status
      let action = '';
      if (forApprove.length === data.length) {
        action = 'approve';
      } else {
        action = 'change-status';
      }

      // If this action is not allowed assign empty
      if (!this.localPolicyOptions.actions[action]) {
        action = 'empty';
      }
      return action;
    },
    formatActionsForChangeStatus() {
      let action = 'change-status';
      // If this action is not allowed assign empty
      if (!this.localPolicyOptions.actions[action]) {
        action = 'empty';
      }
      return action;
    },
    mustShowAction(content) {
      return (
        (content === 'change-status' || content === 'approve')
        // In the one client view we only show the gear icon with the fund
        //  setting menu that already includes the "Change Status" option
        && !(content === 'change-status' && this.isOneClientView)
      );
    },
    showToolTip(event, message) {
      this.$emit('showToolTip', event, message, undefined, 'small');
    },
    hideToolTip() {
      this.$emit('hideToolTip');
    },
    showTooltipAndLoadStatus(event, message, row, content) {
      // check the status is multiple or not
      if ((content && content.length > 0)) {
        // toggle hover true to show tooltip
        this.$set(this.rows[row], 'toggle', true);
        // check if status data is stored in row object property
        const isStatusDataPresent = has(this.rows[row], 'statusDetails');
        if (!isStatusDataPresent) {
          this.$set(this.rows[row], 'loadingStatus', true);
          this.$set(this.rows[row], 'statusDetails', []);
          // call API and store the message in row object property
          const queryParams = `&fiduciaryID=${this.selectedFiduciary}&SecID=${this.rows[row].secID}`;
          UniverseFundsService.getAllClientStatusDetails(queryParams).then((response) => {
            this.$set(this.rows[row], 'loadingStatus', false);
            if (response.status.success) {
              const productionCountArr = response.data.productionStatusCount;
              const pendingCountArr = response.data.pendingStatusCount;
              const productionCount = productionCountArr.map((status) => {
                return {
                  // eslint-disable-next-line max-len
                  statusText: `${FUND_STATUSES[status.fundStatusID]} - ${status.clientCounts} ${status.clientCounts > 1 ? 'clients' : 'client'}`,
                  isPending: false,
                };
              });
              const pendingCount = pendingCountArr.map((status) => {
                // eslint-disable-next-line max-len
                return {
                  // eslint-disable-next-line max-len
                  statusText: `${FUND_STATUSES[status.fundStatusID]} - ${status.clientCounts} ${status.clientCounts > 1 ? 'clients' : 'client'}`,
                  isPending: true,
                };
              });
              const formattedStatus = [...productionCount, ...pendingCount];
              this.$set(this.rows[row], 'statusDetails', formattedStatus);
            }
          }).catch((error) => {
            this.$set(this.rows[row], 'loadingStatus', false);
            this.$set(this.rows[row], 'toggle', false);
            delete this.rows[row].statusDetails;
            this.handleErrorForStatus(error);
          });
        }
      } else {
        this.showToolTip(event, message);
      }
    },
    handleErrorForStatus(error) {
      this.$store.dispatch('openNotification', {
        message: error.message,
        notificationType: 'error',
      });
    },
    hideMultiTooltipOnLeave(row) {
      this.$set(this.rows[row], 'toggle', false);
    },
    getTooltipMessage(content) {
      return this.actionsSchema[content].tooltipMessage;
    },
    redirectToReviewStatusAcrossClients(fundId, fiduciaryFundId) {
      this.$router.push(
        {
          name: 'reviewStatusAcrossClients',
          params: { fundId, fiduciaryFundId: fiduciaryFundId.toString() },
        },
      );
      this.hideToolTip();
    },
  },
  watch: {
    selectedClient() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = DEFAULT_FIRST_PAGE;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    filterStatusSize() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = DEFAULT_FIRST_PAGE;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    filterCategoriesSize() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = DEFAULT_FIRST_PAGE;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    filterAssetsSize() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = DEFAULT_FIRST_PAGE;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    filterReviewStatusSize() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = DEFAULT_FIRST_PAGE;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    sortColumn() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = DEFAULT_FIRST_PAGE;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    sortDirection() {
      this.$store.dispatch('setTotalRows', 0);
      this.currentPage = DEFAULT_FIRST_PAGE;
      this.rows = [];
      this.setIsLoading(true);
      this.loadData();
    },
    searchTerm() {
      this.debouncedSearchFunds();
    },
    scrolled(value) {
      // Check 'this.totalPages - 1' for upper limit as page number start with 0
      if (value && !this.isLoading && this.currentPage < (this.totalPages - 1)) {
        this.currentPage += 1;
        this.setIsLoading(true);
        this.loadData();
      }
    },
    fullscreenModalShow(newValue, oldValue) {
      const closed = oldValue === true && newValue === false;
      if (closed && this.fullscreenModalPerformAction) {
        this.$store.dispatch('setTotalRows', 0);
        this.currentPage = DEFAULT_FIRST_PAGE;
        this.rows = [];
        this.setIsLoading(true);
        this.loadData();
      }
    },
  },
  beforeDestroy() {
    this.requestController && this.requestController.cancel(this.abortRequestKey);
    this.setIsLoading(false);
  },
};
</script>
<style lang="scss">
.grid-universe .grid {
  height: 1px; // Flex fix
  padding: 0 $mds-space-4-x;
  position: relative;
}
.grid-universe .mds-data-table {
  width: 100%;
  .button-icon {
    svg {
      @include mds-icon-color($mds-color-neutral-80);
    }
  }
}
.grid-universe .mds-data-table__header-cell {
  vertical-align: top;
}
.grid-universe .mds-data-table__header-cell[aria-sort] {
  cursor: pointer;
}
.grid-universe .mds-data-table__header-cell-inner {
  padding-top: $mds-space-1-x;
}
.grid-universe .mds-data-table__header-cell-inner[role="button"]:focus {
  box-shadow: none;
}
.grid-universe .mds-data-table__cell {
  vertical-align: middle;
}

// Specific Grid table styles
.grid-universe {
  .level-0:hover,
  .level-1:hover {
    box-shadow: 0 0 4px 0 rgba(70, 60, 60, 0.08), 0 2px 4px 0 rgba(0, 0, 0, 0.08);
    .mds-data-table__cell {
      background: $mds-table-row-hover-color;
    }
  }
  .level-0:hover,
  .level-1:hover {
    &__undo .baseGridIcon {
      background-color: #e0f3fb;

      svg {
        @include mds-icon-color($mds-color-neutral-37);
      }
    }
  }
  .fund-series {
    padding: 0 $mds-space-3-x;
  }
  .fund-series-name {
    font-weight: bold;
    color: $mds-color-black;
  }
  .fund-series_item {
    padding: 0 $mds-space-5-x;
    line-height: 28px;
  }
  .mds-data-table {
    tr.mds-data-table__row {
      &.parent-row,
      &.child-row {
        background-color: $mds-color-neutral-95;
      }
    }
  }
}

// Icon common styles
.grid-universe {
  .mds-data-table__row .baseGridIcon {
    width: $mds-space-inset-4-x;
    height: $mds-space-inset-4-x;
    border-radius: 6px;

    svg {
      @include mds-icon-color($mds-color-neutral-80);
    }
  }
}

// Action icon
.grid-universe {
  &__action-icon:hover .baseGridIcon {
    background-color: #e0f3fb;

    svg {
      @include mds-icon-color($mds-color-neutral-37);
    }
  }
}

// Preferred icon
.grid-universe {
  .star .baseGridIcon svg {
    @include mds-icon-color($mds-interactive-color-primary-default);
  }

  &__preferred-icon:hover .baseGridIcon {
    background-color: #e0f3fb;

    svg {
      @include mds-icon-color($mds-color-neutral-37);
    }
  }
}

// Fix Header
.fixed-header-table {
  position: relative;
}
.fixed-header-table .mds-data-table__header th {
  background-color: $mds-color-white;
}
.fixed-header-table .mds-data-table__header th {
  position: sticky;
  top: 0;
  z-index: 100;
}

// Loader
.loading-spinner {
  padding-bottom: $mds-space-2-x;
}

.title-message {
  @include mds-level-1-heading();
  font-weight: $mds-font-weight-thin;
  color: $mds-text-color-primary-on-light;
  margin: $mds-space-8-x auto;
  text-align: center;
  width: 50%;
}

.pending-series-icon{
  padding: $mds-space-three-quarter-x 0 0 $mds-space-half-x;
}

.status-list {
  margin: 0;
  padding: 0;
  list-style-type: none;

  .status-list-item {
    margin-bottom: $mds-space-half-x;
  }

  .pending-tooltip-icon{
    margin-right: $mds-space-half-x;
  }
}

.clear-both {
  clear: both;
}

.review-status-column {
  cursor: pointer;
}
</style>
